import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { motion } from "framer-motion";

import {
  FaNodeJs,
  FaServer,
  FaDatabase,
  FaCloud,
  FaCode,
  FaCogs,
  FaRocket,
  FaMobileAlt,
} from "react-icons/fa";
import { Link } from "react-router-dom";

// Topics
const topics = [
  {
    icon: <FaNodeJs />,
    title: "Node Basics",
    description:
      "Understand the Node runtime, event loop, and backend fundamentals.",
  },
  {
    icon: <FaServer />,
    title: "Express.js",
    description: "Build RESTful APIs and server-side applications.",
  },
  {
    icon: <FaDatabase />,
    title: "Database Integration",
    description: "Connect Node apps with MongoDB, MySQL, PostgreSQL.",
  },
  {
    icon: <FaCode />,
    title: "Async Programming",
    description: "Master callbacks, promises, and async/await.",
  },
  {
    icon: <FaCogs />,
    title: "Middleware & Modules",
    description: "Use npm packages and middleware effectively.",
  },
  {
    icon: <FaRocket />,
    title: "Real-time Apps",
    description: "Create chat apps, dashboards, and live collaboration tools.",
  },
];

// Usage Areas
const usageAreas = [
  {
    icon: <FaServer />,
    title: "Web Applications",
    description: "Develop scalable, fast server-side web apps.",
  },
  {
    icon: <FaCloud />,
    title: "Cloud & Serverless",
    description: "Deploy Node apps on AWS, Azure, or serverless platforms.",
  },
  {
    icon: <FaDatabase />,
    title: "APIs & Backend Services",
    description: "Build REST and GraphQL APIs for apps.",
  },
  {
    icon: <FaMobileAlt />,
    title: "Mobile Backend",
    description: "Power mobile apps with robust backends.",
  },
  {
    icon: <FaRocket />,
    title: "Real-time Collaboration",
    description: "Create chat apps and live collaborative tools.",
  },
  {
    icon: <FaCogs />,
    title: "IoT & Automation",
    description: "Use Node for IoT devices and automation.",
  },
];

// Future Scope
const futureScope = [
  {
    icon: "🚀",
    title: "Serverless & Cloud",
    text: "Node is central to cloud and serverless architectures.",
  },
  {
    icon: "🌐",
    title: "Fullstack JavaScript",
    text: "Combine Node with React, Vue, or Angular for end-to-end apps.",
  },
  {
    icon: "📱",
    title: "Mobile Backend",
    text: "Node powers APIs for mobile applications globally.",
  },
  {
    icon: "🔒",
    title: "Enterprise Adoption",
    text: "Large companies use Node for scalable systems.",
  },
];

const NodeAnimation = () => {
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  return (
    <div
      style={{
        background: "#fff",
        color: "#222",
      }}
      className="overflow-hidden"
    >
      {/* HEADER */}
      <header
        style={{
          position: "relative",
          padding: "100px 20px",
          textAlign: "center",
          background:
            "linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url('../assets/photo-1555066931-4365d14bab8c.AVIF') center/cover no-repeat",
          color: "#fff",
        }}
      >
        <h1
          className="text-4xl md:text-6xl font-extrabold mb-4"
          data-aos="fade-down"
        >
          Node Development
        </h1>
        <p
          data-aos="fade-up"
          style={{ marginTop: "10px" }}
          className="text-lg md:text-xl"
        >
          Learn server-side JavaScript, backend systems, APIs, and career growth
        </p>

        {/* ✅ Enroll Button in Header */}
        <Link
          to="/contact"
          className="mt-6 inline-block px-8 py-3 bg-yellow-400 text-black rounded-full font-bold shadow-md hover:bg-yellow-500 transition animate-bounce"
          data-aos="fade-up"
        >
          Enroll Now
        </Link>
      </header>

      {/* WHAT WE SHOULD LEARN */}
      <section
        style={{ padding: "60px 20px", maxWidth: "1200px", margin: "0 auto" }}
      >
        <h2
          style={{
            textAlign: "center",
            marginBottom: "40px",
            fontSize: "2.5rem",
            fontWeight: "bold",
          }}
          data-aos="fade-up"
        >
          What We Should Learn
        </h2>
        <div
          style={{
            display: "flex",
            gap: "30px",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {/* LEFT SIDE: Topics */}
          <div
            style={{ flex: "1 1 300px", minWidth: "280px", maxWidth: "600px" }}
          >
            {topics.map((topic, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "20px",
                  background: "#f8f8f8",
                  padding: "15px",
                  borderRadius: "10px",
                  cursor: "pointer",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                }}
                data-aos="fade-up"
                data-aos-delay={i * 100}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-5px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 20px rgba(0, 136, 63, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <motion.div
                  style={{
                    fontSize: "2rem",
                    marginRight: "15px",
                    color: "#68a063",
                    flexShrink: 0,
                  }}
                  animate={{
                    rotate: [0, 10, -10, 0], // shake effect
                    scale: [1, 1.2, 1, 1.2, 1], // pulse
                    opacity: [1, 0.7, 1], // blink
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "loop",
                    ease: "easeInOut",
                  }}
                >
                  {topic.icon}
                </motion.div>
                <div>
                  <h4 style={{ margin: 0, fontWeight: "bold" }}>
                    {topic.title}
                  </h4>
                  <p style={{ fontSize: "0.95rem", color: "#555" }}>
                    {topic.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* RIGHT SIDE: Node.js Image */}
          <div
            style={{
              flex: "1 1 300px",
              minWidth: "280px",
              maxWidth: "600px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            data-aos="fade-left"
          >
            <img
              src="../assets/Node_js_Architecture_A_Comprehensive_Guide_1_af37a73e1e.png"
              alt="Node.js"
              style={{
                width: "100%",
                maxWidth: "450px",
                borderRadius: "15px",
                boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              }}
            />
          </div>
        </div>
      </section>

      {/* WHERE IT CAN BE USED */}
      <section className="py-5 bg-light">
        <h2
          style={{
            textAlign: "center",
            marginBottom: "40px",
            fontSize: "2.5rem",
            fontWeight: "bold",
          }}
          data-aos="fade-up"
        >
          Where Node Can Be Used
        </h2>
        <div className="container">
          <div className="row g-4">
            {usageAreas.map((area, i) => {
              const colors = [
                "#68a063",
                "#ffa500",
                "#00bcd4",
                "#ff5722",
                "#9c27b0",
                "#2196f3",
              ];
              return (
                <div
                  key={i}
                  className="col-sm-6 col-lg-4"
                  data-aos="zoom-in-up"
                  data-aos-delay={i * 100}
                >
                  <div
                    className="p-4 text-center rounded position-relative h-100 usage-card"
                    style={{
                      background: "#fff",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
                      transition: "transform 0.3s ease",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.querySelector("h5").style.color =
                        "#68a063"; // turn green
                      e.currentTarget.style.transform = "translateY(-5px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.querySelector("h5").style.color = "#222"; // reset to default
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: "45px",
                        height: "45px",
                        background: `linear-gradient(135deg, ${
                          colors[i % colors.length]
                        }, transparent)`,
                        borderTopRightRadius: "15px",
                        boxShadow: `0 0 10px ${colors[i % colors.length]}`,
                      }}
                    ></div>
                    <div
                      style={{
                        fontSize: "2rem",
                        color: colors[i % colors.length],
                      }}
                    >
                      {area.icon}
                    </div>
                    <h5
                      className="fw-bold"
                      style={{ transition: "color 0.3s ease" }}
                    >
                      {area.title}
                    </h5>
                    <p className="text-muted small">{area.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FUTURE SCOPE */}
      <section
        style={{
          padding: "60px 20px",
          background: "#fff",
          color: "#333",
          textAlign: "center",
          overflow: "hidden",
        }}
      >
        <h2
          style={{
            fontSize: "2.5rem",
            fontWeight: "bold",
            marginBottom: "30px",
          }}
          data-aos="fade-up"
        >
          Future Scope
        </h2>

        <div
          id="my-sc"
          style={{ display: "flex", width: "100%", overflow: "hidden" }}
        >
          {[0, 1].map((groupIndex) => (
            <div
              key={groupIndex}
              className="scroll-group"
              style={{ display: "flex", gap: "20px", flexShrink: 0 }}
            >
              {futureScope.map((item, i) => {
                const colors = ["#ff5722", "#673ab7", "#3f51b5", "#009688"];
                return (
                  <div
                    key={i}
                    style={{
                      position: "relative",
                      background: "linear-gradient(145deg, #fdfdfd, #f3f3f3)",
                      borderRadius: "20px",
                      padding: "25px",
                      boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                      border: "1px solid #eee",
                      transition: "transform 0.3s ease",
                      minWidth: "250px",
                      flexShrink: 0,
                      animation: "shake 3s infinite ease-in-out",
                      animationDelay: `${i * 500}ms`,
                      color: "#333",
                    }}
                    data-aos="fade-up"
                    data-aos-delay={i * 150}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: "35px",
                        height: "35px",
                        background: `linear-gradient(135deg, ${
                          colors[i % colors.length]
                        }, transparent)`,
                        borderTopRightRadius: "20px",
                        boxShadow: `0 0 10px ${colors[i % colors.length]}`,
                      }}
                    ></div>
                    <div style={{ fontSize: "2rem", marginBottom: "10px" }}>
                      {item.icon}
                    </div>
                    <h3
                      style={{
                        fontSize: "1.3rem",
                        fontWeight: "bold",
                        marginBottom: "10px",
                      }}
                    >
                      {item.title}
                    </h3>
                    <p style={{ fontSize: "0.95rem", color: "#555" }}>
                      {item.text}
                    </p>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <style jsx>{`
          #my-sc {
            overflow: hidden;
            white-space: nowrap;
          }
          .scroll-group {
            animation: marquee 20s linear infinite;
          }
          @keyframes marquee {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-50%);
            }
          }
          @keyframes shake {
            0%,
            100% {
              transform: translate(0, 0) rotate(0);
            }
            20% {
              transform: translate(-1px, 1px) rotate(-0.5deg);
            }
            40% {
              transform: translate(1px, -1px) rotate(0.5deg);
            }
            60% {
              transform: translate(-1px, 1px) rotate(-0.5deg);
            }
            80% {
              transform: translate(1px, -1px) rotate(0.5deg);
            }
          }
        `}</style>
      </section>

      {/* FOOTER */}
      <footer className="py-6 text-center text-white bg-dark mt-5">
        <div className="container">
          {/* Enroll Call-to-Action */}
          <h5 className="fw-bold text-success mb-3">
            🚀 Ready to Master Node.js?
          </h5>
          <p className="text-light mb-4">
            Join thousands of developers learning Node.js step by step. Enroll
            now and start building scalable applications today!
          </p>
          <Link
            to="/contact"
            className="btn btn-success px-4 py-2 fw-bold shadow-sm"
            style={{ borderRadius: "30px" }}
          >
            Enroll Now
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default NodeAnimation;
